import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown, faCreditCard, faEdit, faHashtag, faMoneyBill,
    faPowerOff, faRefresh, faSave, faStore, faTimes, faUniversity,
    faUser, faWallet
} from '@fortawesome/free-solid-svg-icons';
import '../module_mechanics/Styles/Form.css';
import '../module_mechanics/Styles/FormList.css';
import '../module_mechanics/Styles/mechanics_accounts.css';

const ACCOUNTS_API = 'https://ourzestapp.online/oz_backend/mechanics_module/mechanics_account.php';
const emptyForm = {
    account_id: null,
    account_type: 'digital_wallet',
    provider_code: '',
    provider_name: '',
    account_title: '',
    account_number: '',
    iban: '',
    opening_balance: '0'
};

const amount = (value) => Number(value || 0).toLocaleString('en-PK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const ACCOUNT_TYPE_OPTIONS = [
    { value: 'digital_wallet', label: 'Digital wallet' },
    { value: 'bank_account', label: 'Bank account' }
];

function ProductCustomDropdown({ id, label, icon, value, onChange, options, disabled = false, emptyText = 'No options found' }) {
    const rootRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const selected = options.find((option) => String(option.value) === String(value ?? ''));
    const filteredOptions = options.filter((option) => String(option.label).toLowerCase().includes(query.trim().toLowerCase()));

    useEffect(() => {
        if (!open) setQuery(selected?.label || '');
    }, [open, selected?.label]);

    useEffect(() => {
        const closeOutside = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', closeOutside);
        return () => document.removeEventListener('mousedown', closeOutside);
    }, []);

    const choose = (option) => {
        onChange(option.value);
        setQuery(option.label);
        setOpen(false);
        setActiveIndex(0);
    };

    const handleKeyDown = (event) => {
        if (disabled) return;
        if (event.key === 'Escape') {
            setOpen(false);
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            const movement = event.key === 'ArrowDown' ? 1 : -1;
            setActiveIndex((current) => Math.max(0, Math.min(filteredOptions.length - 1, current + movement)));
            return;
        }
        if (event.key === 'Enter' && open && filteredOptions[activeIndex]) {
            event.preventDefault();
            choose(filteredOptions[activeIndex]);
        }
    };

    return (
        <div className="ozui-field ozacct-custom-dropdown" ref={rootRef}>
            <div className="ozui-input-wrapper ozui-category-wrapper">
                <input
                    id={id}
                    type="text"
                    value={open ? query : (selected?.label || '')}
                    onChange={(event) => { setQuery(event.target.value); setOpen(true); setActiveIndex(0); }}
                    onFocus={() => { setQuery(selected?.label || ''); setOpen(true); setActiveIndex(0); }}
                    onKeyDown={handleKeyDown}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={`${id}_options`}
                />
                <label htmlFor={id}><FontAwesomeIcon icon={icon} />{label}</label>
                <FontAwesomeIcon icon={faChevronDown} className={`ozui-dropdown-arrow ${open ? 'ozacct-dropdown-open' : ''}`} />
            </div>
            {open && !disabled && (
                <div className="ozui-category-dropdown" id={`${id}_options`} role="listbox">
                    {filteredOptions.length ? filteredOptions.map((option, index) => (
                        <div
                            key={String(option.value)}
                            className={`ozui-category-option ${String(value ?? '') === String(option.value) ? 'ozui-selected' : ''} ${index === activeIndex ? 'ozacct-option-active' : ''}`}
                            onMouseEnter={() => setActiveIndex(index)}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => choose(option)}
                            role="option"
                            aria-selected={String(value ?? '') === String(option.value)}
                        >{option.label}</div>
                    )) : <div className="ozui-category-empty">{emptyText}</div>}
                </div>
            )}
        </div>
    );
}

async function request(payload) {
    const response = await fetch(ACCOUNTS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
    });
    const text = await response.text();
    let data;
    try { data = JSON.parse(text); }
    catch { throw new Error(`Accounts API returned invalid JSON (HTTP ${response.status})`); }
    if (!response.ok || data.status !== 'success') {
        throw new Error(data.message || `Request failed (HTTP ${response.status})`);
    }
    return data;
}

export default function MechanicsAccounts() {
    const { user, currentShop } = useAuth();
    const [catalogs, setCatalogs] = useState({ digital_wallets: [], banks: [] });
    const [accounts, setAccounts] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const basePayload = useMemo(() => ({
        shop_id: currentShop?.shop_id,
        username: user?.username
    }), [currentShop?.shop_id, user?.username]);

    const loadAccounts = useCallback(async () => {
        if (!basePayload.shop_id || !basePayload.username) return;
        setLoading(true);
        try {
            const result = await request({ action: 'list_accounts', active_only: false, ...basePayload });
            setAccounts(Array.isArray(result.data) ? result.data : []);
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setLoading(false);
        }
    }, [basePayload]);

    useEffect(() => {
        request({ action: 'catalogs' })
            .then((result) => setCatalogs(result.data || { digital_wallets: [], banks: [] }))
            .catch((error) => setMessage({ type: 'error', text: error.message }));
    }, []);

    useEffect(() => { loadAccounts(); }, [loadAccounts]);

    const providers = form.account_type === 'digital_wallet'
        ? catalogs.digital_wallets : catalogs.banks;
    const providerOptions = providers.map((provider) => ({ value: provider.code, label: provider.name }));

    const selectProvider = (code) => {
        const provider = providers.find((item) => item.code === code);
        setForm((current) => ({
            ...current,
            provider_code: code,
            provider_name: code === 'other' ? '' : (provider?.name || '')
        }));
    };

    const save = async (event) => {
        event.preventDefault();
        if (!basePayload.shop_id) {
            setMessage({ type: 'error', text: 'Select a shop first.' });
            return;
        }
        setSaving(true);
        setMessage(null);
        try {
            await request({ action: 'save_account', ...basePayload, ...form });
            setMessage({ type: 'success', text: 'Account saved successfully.' });
            setForm(emptyForm);
            await loadAccounts();
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setSaving(false);
        }
    };

    const edit = (account) => setForm({
        account_id: account.account_id,
        account_type: account.account_type,
        provider_code: account.provider_code,
        provider_name: account.provider_name,
        account_title: account.account_title,
        account_number: account.account_number,
        iban: account.iban || '',
        opening_balance: account.opening_balance || '0'
    });

    const toggle = async (account) => {
        try {
            await request({
                action: 'set_active', ...basePayload, account_id: account.account_id,
                is_active: Number(account.is_active) !== 1
            });
            await loadAccounts();
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        }
    };

    return (
        <div className="ozui-container ozlistui-container ozacct-page">
            <div className="ozlistui-sticky-header">
                <div className="ozlistui-header-bg-animation" aria-hidden="true">
                    <div className="gradient-orb orb-1" />
                    <div className="gradient-orb orb-2" />
                    <div className="gradient-orb orb-3" />
                    <div className="gradient-orb orb-4" />
                    <div className="gradient-orb orb-5" />
                </div>
                <div className="ozlistui-header-content">
                    <div className="ozlistui-header-left">
                        <div className="ozlistui-header-title">
                            <h1>Payment Accounts</h1>
                            <p>Define the wallets and bank accounts used to receive and send payments.</p>
                        </div>
                    </div>
                    
                </div>
            </div>

            {message && <div className={`ozacct-alert ${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>{message.text}</div>}

            <main className="ozacct-layout">
                <section className="ozacct-panel ozacct-form-panel">
                    <div className="ozacct-panel-heading">
                        <span><FontAwesomeIcon icon={form.account_id ? faEdit : faCreditCard} /></span>
                        <div><h2>{form.account_id ? 'Edit account' : 'Add account'}</h2><p>Enter the account details below.</p></div>
                    </div>

                    <form className="ozui-form ozacct-form" onSubmit={save}>
                        <div className="ozui-form-row">
                            <ProductCustomDropdown id="account_type" label="Account type" icon={faWallet} value={form.account_type} onChange={(value) => setForm({ ...emptyForm, account_type: value })} options={ACCOUNT_TYPE_OPTIONS} disabled={saving} />
                            <ProductCustomDropdown id="provider" label="Provider" icon={form.account_type === 'bank_account' ? faUniversity : faWallet} value={form.provider_code} onChange={selectProvider} options={providerOptions} disabled={saving} emptyText="No provider found" />

                            {form.provider_code === 'other' && <div className="ozui-field"><div className="ozui-input-wrapper">
                                <input id="provider_name" value={form.provider_name} onChange={(event) => setForm({ ...form, provider_name: event.target.value })} maxLength="150" placeholder=" " disabled={saving} required />
                                <label htmlFor="provider_name"><FontAwesomeIcon icon={faUniversity} />Custom provider name</label>
                            </div></div>}

                            <div className="ozui-field"><div className="ozui-input-wrapper">
                                <input id="account_title" value={form.account_title} onChange={(event) => setForm({ ...form, account_title: event.target.value })} maxLength="150" placeholder=" " disabled={saving} required />
                                <label htmlFor="account_title"><FontAwesomeIcon icon={faUser} />Account title</label>
                            </div></div>

                            <div className="ozui-field"><div className="ozui-input-wrapper">
                                <input id="account_number" value={form.account_number} onChange={(event) => setForm({ ...form, account_number: event.target.value })} maxLength="100" placeholder=" " disabled={saving} required />
                                <label htmlFor="account_number"><FontAwesomeIcon icon={faHashtag} />Account / mobile number</label>
                            </div></div>

                            {form.account_type === 'bank_account' && <div className="ozui-field"><div className="ozui-input-wrapper">
                                <input id="account_iban" value={form.iban} onChange={(event) => setForm({ ...form, iban: event.target.value.toUpperCase() })} maxLength="24" placeholder=" " disabled={saving} />
                                <label htmlFor="account_iban"><FontAwesomeIcon icon={faUniversity} />IBAN (optional)</label>
                            </div></div>}

                            {!form.account_id && <div className="ozui-field"><div className="ozui-input-wrapper">
                                <input id="opening_balance" type="number" min="0" step="0.01" value={form.opening_balance} onChange={(event) => setForm({ ...form, opening_balance: event.target.value })} placeholder=" " disabled={saving} />
                                <label htmlFor="opening_balance"><FontAwesomeIcon icon={faMoneyBill} />Opening balance</label>
                            </div></div>}
                        </div>

                        <div className="ozui-form-actions">
                            {form.account_id && <button className="ozui-btn ozacct-cancel-btn" type="button" onClick={() => setForm(emptyForm)} disabled={saving}><FontAwesomeIcon icon={faTimes} /><span>Cancel</span></button>}
                            <button className="ozui-btn ozui-btn-primary" type="submit" disabled={saving}><FontAwesomeIcon icon={faSave} /><span>{saving ? 'Saving…' : 'Save account'}</span></button>
                        </div>
                    </form>
                </section>

                <section className="ozacct-panel ozacct-list-panel">
                    <div className="ozacct-panel-heading ozacct-list-heading">
                        <span><FontAwesomeIcon icon={faCreditCard} /></span>
                        <div><h2>Configured accounts</h2><p>{accounts.length} wallet and bank {accounts.length === 1 ? 'account' : 'accounts'}.</p></div>
                    </div>

                    {loading ? <div className="ozacct-empty" role="status">Loading accounts…</div> : accounts.length === 0 ? <div className="ozacct-empty">No wallet or bank account configured yet.</div> : (
                        <div className="ozlistui-table-container ozacct-table-wrap">
                            <div className="ozlistui-table-wrapper">
                                <table className="ozlistui-table ozacct-table">
                                    <thead><tr><th>Provider / account</th><th>Type</th><th>Balance</th><th>Status</th><th className="ozlistui-actions-column">Actions</th></tr></thead>
                                    <tbody>
                                        {accounts.map((account) => {
                                            const active = Number(account.is_active) === 1;
                                            return <tr key={account.account_id} className={active ? '' : 'ozacct-inactive'}>
                                                <td data-label="Provider / account"><div className="ozacct-account-cell"><strong>{account.provider_name}</strong><small>{account.account_title} · {account.account_number}</small></div></td>
                                                <td data-label="Type"><span className="ozacct-type"><FontAwesomeIcon icon={account.account_type === 'digital_wallet' ? faWallet : faUniversity} />{account.account_type === 'digital_wallet' ? 'Wallet' : 'Bank'}</span></td>
                                                <td data-label="Balance" className="ozacct-balance">Rs. {amount(account.current_balance)}</td>
                                                <td data-label="Status"><span className={`ozacct-status ${active ? 'active' : ''}`}>{active ? 'Active' : 'Inactive'}</span></td>
                                                <td data-label="Actions" className="ozlistui-actions-cell ozacct-row-actions">
                                                    <div className="ozacct-action-group">
                                                        <button className="ozlistui-action-btn ozlistui-edit-btn" type="button" onClick={() => edit(account)} title="Edit account"><FontAwesomeIcon icon={faEdit} /><span>Edit</span></button>
                                                        <button className={`ozlistui-action-btn ${active ? 'ozacct-disable-btn' : 'ozacct-enable-btn'}`} type="button" onClick={() => toggle(account)} title={active ? 'Disable account' : 'Enable account'}><FontAwesomeIcon icon={faPowerOff} /><span>{active ? 'Disable' : 'Enable'}</span></button>
                                                    </div>
                                                </td>
                                            </tr>;
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
}

